import { FC, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Button, Divider, Stack } from '@mui/material';
import moment from 'moment';

import { AvailableResources, CfsApi, UnitApi } from '@/api';
import {
  ButtonWithDoubleClickPrevention,
  DateTimePicker,
  FormRoot,
  FormSelectField,
  RenderFormField,
} from '@/components';
import { withLoader, WithLoaderProps } from '@/hocs';
import {
  ICFS,
  IUnit,
  IUnitShiftForm,
  ResourceType,
  UnitShiftStatus,
} from '@/models';
import { ConfirmTypes, useToastStore } from '@/store';

import { AppModal, BaseModalProps, UnitResourceForm } from '../..';

interface UnitShiftAssignForm extends IUnitShiftForm {
  cfsId: string;
  reportTime?: Date;
}

export interface UnitShiftAssignModalProps
  extends Omit<BaseModalProps, 'children' | 'onClose'>,
    WithLoaderProps {
  cfsId?: string;
  unitShiftId?: string;
  unit?: IUnit;
  assignToHQ?: boolean;
  headquarterName?: string | null;
  setOnDuty?: boolean;
  isEditMode?: boolean;
  showScheduleOption?: boolean;
  onClose: (refresh?: boolean) => void;
  handleAssignUnit?: () => void;
}

const UnitShiftAssignModal: FC<UnitShiftAssignModalProps> = ({
  cfsId,
  unitShiftId,
  unit,
  showScheduleOption = false,
  assignToHQ,
  headquarterName,
  setOnDuty,
  isEditMode,
  showLoader,
  hideLoader,
  open,
  ...rest
}) => {
  const { updateToast } = useToastStore();
  const methods = useForm<UnitShiftAssignForm>();
  const { getValues, handleSubmit, reset } = methods;
  const [cfses, setCfses] = useState<ICFS[]>([]);
  const [isScheduling, setIsScheduling] = useState(false);
  const [startAt, setStartAt] = useState<string | undefined>(undefined);
  const [shiftResources, setShiftResources] = useState<
    AvailableResources | undefined
  >(undefined);
  const [isShiftLoaded, setIsShiftLoaded] = useState(false);

  const isStandBy = unit?.isStandByWhenOffDuty;
  const shouldAssignCfs = !setOnDuty && (unitShiftId || isStandBy);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const cfsSelectOptions = useMemo(() => {
    return cfses.map((cfs) => ({ label: cfs.number, value: cfs._id || '' }));
  }, [cfses]);

  useEffect(() => {
    if (shouldAssignCfs && !cfsId) {
      fetchActiveCfses();
    }
  }, [shouldAssignCfs]);

  useEffect(() => {
    if (!open) return;

    if (isEditMode && unitShiftId) {
      setIsShiftLoaded(false);
      const fetchShift = async () => {
        try {
          const res = await UnitApi.getUnitShift(unitShiftId);
          const shift = res.data;
          setShiftResources({
            vehicles: shift.vehicles as any,
            users: shift.users as any,
            equipments: shift.equipments as any,
          });
          reset({
            unit: shift.unit?._id,
            vehicles: shift.vehicles?.map((v) => v._id),
            users: shift.users?.map((u) => u._id),
            equipments: shift.equipments?.map((e) => e._id),
            polygons: shift.polygons,
            lines: shift.lines,
            points: shift.points,
            notes: shift.notes,
          });
        } catch (err: any) {
          updateToast({ open: true, message: err.message });
        } finally {
          setIsShiftLoaded(true);
        }
      };
      fetchShift();
    } else {
      setIsShiftLoaded(true);
      setShiftResources(undefined);
      reset(
        unit
          ? {
              unit: unit._id,
              vehicles: unit.vehicles?.map((v: any) => v._id),
              users: unit.users?.map((u: any) => u._id),
              equipments: unit.equipments?.map((e: any) => e._id),
              polygons: unit.polygons,
              lines: unit.lines,
              points: unit.points,
              notes: unit.notes,
            }
          : {},
      );
    }
  }, [open]);

  const fetchActiveCfses = async () => {
    try {
      const res = await CfsApi.activeList({
        sort: JSON.stringify({ createdAt: -1 }),
      });
      setCfses(res.data);
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  const validateCfs = async (targetCfsId: string) => {
    try {
      const cfsDetails = await CfsApi.getOne(targetCfsId);
      if (!cfsDetails.data.addressInfo?.address) {
        updateToast({
          type: ConfirmTypes.WARNING,
          open: true,
          message: 'Address is required to assign a unit shift to CFS',
        });
        return false;
      }
      return true;
    } catch (err: any) {
      updateToast({
        type: ConfirmTypes.ERROR,
        open: true,
        message: err.message,
      });
      return false;
    }
  };

  const onSubmit = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    showLoader();

    try {
      const values = getValues();

      if (isEditMode && unitShiftId) {
        await UnitApi.updateUnitShifts(unitShiftId, {
          vehicles: values.vehicles,
          users: values.users,
          equipments: values.equipments,
          polygons: values.polygons,
          lines: values.lines,
          points: values.points,
          notes: values.notes,
        });
        updateToast({
          type: ConfirmTypes.SUCCESS,
          open: true,
          message: 'Unit resources updated successfully.',
        });
        rest.onClose(true);
        return;
      }

      if (shouldAssignCfs) {
        const targetCfsId = cfsId || values.cfsId;
        const isValid = await validateCfs(targetCfsId);

        if (!isValid) {
          hideLoader();
          setIsSubmitting(false);
          return;
        }
      }

      let _unitShiftId = unitShiftId;
      let isScheduled = false;
      if (unit) {
        const res = await UnitApi.createUnitShift({
          ...values,
          startAt,
          wasOnDutiedFromCFS: !!shouldAssignCfs,
        });
        updateToast({
          type: ConfirmTypes.SUCCESS,
          open: true,
          message: 'Unit shift is now on-duty',
        });
        _unitShiftId = res.data._id;
        isScheduled = res?.data?.status === UnitShiftStatus.SCHEDULED;
      }

      if (shouldAssignCfs && _unitShiftId) {
        await CfsApi.assignUnitShift(
          cfsId || values.cfsId,
          _unitShiftId,
          assignToHQ,
          headquarterName,
        );
        updateToast({
          type: ConfirmTypes.SUCCESS,
          open: true,
          message: 'Unit shift successfully assigned',
        });
      } else {
        updateToast({
          type: ConfirmTypes.SUCCESS,
          open: true,
          message: isScheduled
            ? 'The unit has been successfully scheduled to move to on-duty.'
            : 'Unit shift is now on-duty',
        });
      }

      rest.handleAssignUnit?.();
      rest.onClose(true);
    } catch (err: any) {
      updateToast({
        open: true,
        message: (err.message as string)
          .replaceAll(/\s[a-z]*:/g, '')
          .replace(ResourceType.PEOPLE, 'Employees')
          .replace(ResourceType.VEHICLE, 'Vehicles')
          .replace(ResourceType.EQUIPMENT, 'Equipment'),
      });
    } finally {
      hideLoader();

      setTimeout(() => {
        setIsSubmitting(false);
      }, 1000);
    }
  };

  const noteFormFieldValidator = (value?: string): true | string => {
    if (!value) return true;

    const wordCount = value.trim().split(/\s+/).length;
    return wordCount <= 30 || 'Note must not exceed 30 words';
  };

  return (
    <AppModal
      title={unit ? unit.name : 'Select CFS'}
      {...rest}
      open={open}
      classes={{ content: 'fixed-width' }}
    >
      <FormProvider {...methods}>
        <FormRoot onSubmit={handleSubmit(onSubmit)} sx={{ display: 'block' }}>
          {shouldAssignCfs && !cfsId && (
            <FormSelectField
              label="CFS"
              name="cfsId"
              items={cfsSelectOptions}
              rules={{ required: 'This field is required' }}
              MenuProps={{
                PaperProps: { sx: { maxHeight: 250, width: 560 } },
              }}
            />
          )}
          {unit && isShiftLoaded && (
            <>
              <UnitResourceForm
                currentUnitType={unit?.type}
                agencyId={unit?.agency}
                cfsId={cfsId}
                unitId={unit?._id}
                resources={shiftResources}
              />
              <RenderFormField
                name="notes"
                label="Notes"
                type="text-area"
                rules={{
                  validate: noteFormFieldValidator,
                }}
              />
            </>
          )}
          <Divider sx={{ my: 3, width: '100%' }} />

          {showScheduleOption && (
            <Stack flex={1} flexDirection="row" justifyContent="flex-start">
              {!isScheduling && (
                <Button onClick={() => setIsScheduling(true)}>Schedule</Button>
              )}

              {isScheduling && (
                <DateTimePicker
                  open={true}
                  onAccept={(v) => setStartAt(moment(v).utc().toISOString())}
                />
              )}
            </Stack>
          )}

          <Stack flex={1} flexDirection="row" justifyContent="flex-end">
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => rest.onClose()}
            >
              Cancel
            </Button>
            <ButtonWithDoubleClickPrevention
              type="submit"
              variant="contained"
              color="error"
              sx={{ ml: 3, px: 2 }}
            >
              {isEditMode ? 'Save' : shouldAssignCfs ? 'Assign' : 'On-duty'}
            </ButtonWithDoubleClickPrevention>
          </Stack>
        </FormRoot>
      </FormProvider>
    </AppModal>
  );
};

export default withLoader(UnitShiftAssignModal);
